import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import request from "../../Helpers/API";
import Swal from "sweetalert2";
import { LoaderScreen } from "../components/LoaderScreen";
import { ChatScreen } from "../Whatsapp/ChatScreen";
import { MensajeriaModalAlerta } from "./MensajeriaModalAlerta";
import { MensajeriaFiltrosPanel } from "./MensajeriaFiltrosPanel";
import { MensajeriaColumnaChats } from "./MensajeriaColumnaChats";
import { mapearContactoARemastered, parseFechaMensajeria, SUCURSALES_CATALOGO } from "./mensajeriaRemasteredHelpers";

const formatoInputDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Roles con acceso al botón "Ver Indicadores"
const ROL_REWR = "REWR";
const ROL_UINM = "UINM";

// Remastered de la pantalla de Mensajería — misma fuente de datos que MensajeriaScreen
// (ContactosMensajeriaMenu/obtenerContactos). Se usa desde MensajeriaListadoScreen en
// lugar de MensajeriaScreen, sin reemplazarla.
export const MensajeriaRemasteredScreen = () => {
    const history = useHistory();
    const year = new Date().getFullYear();

    // "Ver Indicadores" solo visible para roles REWR/UINM — mismo patrón de CRM_DetalleScreen
    const { roles } = useSelector((store) => store.sesion);
    const puedeVerIndicadores = roles.some((rol) => rol.idrol === ROL_REWR || rol.idrol === ROL_UINM);

    const [modalAbierto, setModalAbierto] = useState(true);
    const [contactosPosventa, setContactosPosventa] = useState([]);
    const [contactosCobranza, setContactosCobranza] = useState([]);
    const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

    const [modalChat, setModalChat] = useState(false);
    const [chatActivo, setChatActivo] = useState(null);

    const [filtros, setFiltros] = useState({
        fechaInicio: formatoInputDate(new Date(year, 0, 1)),
        fechaFin: formatoInputDate(new Date()),
        sucursales: [],
        lineas: ["Posventa", "Cobranza"],
    });

    const cargarContactos = () => {
        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get("ContactosMensajeriaMenu/obtenerContactos")
            .then((result) => {
                setContactosPosventa(result.postventa ?? []);
                setContactosCobranza(result.cobranza ?? []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch((error) => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("MENSAJERIA", error, "error");
            });
    };

    useEffect(() => {
        cargarContactos();
    }, []);

    // Aplica la apariencia remastered al ChatScreen (componente compartido con otras pantallas)
    // sin tocar su SCSS global: se scopea vía una clase en <body>, activa solo mientras este
    // chat está abierto — así no afecta el ChatScreen usado en el resto del sistema.
    useEffect(() => {
        document.body.classList.toggle("mjr-chat-scope", modalChat);
        return () => document.body.classList.remove("mjr-chat-scope");
    }, [modalChat]);

    const handleAbrirChat = (chat, modulo) => {
        setChatActivo({ ...chat, modulo });
        setModalChat(true);
    };

    const handleCerrarChat = () => {
        setModalChat(false);
        setChatActivo(null);
        cargarContactos();
    };

    // Catálogo de sucursales para el panel de filtros — combina lo que trae el endpoint con el
    // catálogo fijo de MensajeriaFiltroSucursal para mostrar el nombre en lugar del id.
    // Orden fijo (id terminado en 1 antes que terminado en 2, luego por id) para que la lista
    // no reordene cada vez que se recargan los contactos (ej. al abrir/cerrar un chat).
    const sucursalesDisponibles = [...new Set([...contactosPosventa, ...contactosCobranza].map((c) => c.sucursal))]
        .filter((id) => id !== undefined && id !== null)
        .sort((a, b) => (a % 10) - (b % 10) || a - b)
        .map((id) => ({ id, nombre: SUCURSALES_CATALOGO[id] ?? `Sucursal ${id}` }));

    const inicio = filtros.fechaInicio ? new Date(filtros.fechaInicio + "T00:00:00") : null;
    const fin = filtros.fechaFin ? new Date(filtros.fechaFin + "T23:59:59") : null;

    const aplicarFiltros = (contactos) =>
        contactos.filter((c) => {
            const cumpleSucursal = filtros.sucursales.length === 0 || filtros.sucursales.includes(c.sucursal);
            const fechaMensaje = c.ultimo_mensaje ? parseFechaMensajeria(c.ultimo_mensaje) : null;
            const cumpleFecha =
                (!inicio || (fechaMensaje && fechaMensaje >= inicio)) &&
                (!fin || (fechaMensaje && fechaMensaje <= fin));
            return cumpleSucursal && cumpleFecha;
        });

    const chatsPosventa = aplicarFiltros(contactosPosventa).map(mapearContactoARemastered);
    const chatsCobranza = aplicarFiltros(contactosCobranza).map(mapearContactoARemastered);

    const sinAtenderPosventa = chatsPosventa.filter((c) => c.estado === "sin_atender").length;
    const sinAtenderCobranza = chatsCobranza.filter((c) => c.estado === "sin_atender").length;
    const totalSinResponder = sinAtenderPosventa + sinAtenderCobranza;

    return (
        <div className="crm-main-screen">
            <LoaderScreen loader={loader} />

            {modalAbierto && (
                <MensajeriaModalAlerta
                    total={totalSinResponder}
                    onCerrar={() => setModalAbierto(false)}
                />
            )}

            <div className="crm-body">
                <div className="crm-page">
                    <div className="crm-module">
                        <div className="crm-module-head">
                            <div className="crm-head-grid">
                                <div className="crm-head-left" />
                                <div className="crm-head-center">
                                    <h1 className="crm-module-title">MENSAJERÍA</h1>
                                </div>
                                <div className="crm-head-right">
                                    {puedeVerIndicadores && (
                                        <button
                                            className="crm-btn-action"
                                            onClick={() => history.push("/Mensajeria/Indicadores")}
                                        >
                                            <AssessmentIcon /> Ver Indicadores
                                        </button>
                                    )}
                                    <button
                                        className="crm-btn-action"
                                        onClick={() => history.push("/Mensajeria/Listado")}
                                    >
                                        <VisibilityIcon /> Ver Mensajería
                                    </button>
                                </div>
                            </div>
                            <div className="crm-head-divider">
                                <div className="crm-head-divider-dot" />
                            </div>
                        </div>

                        <div className="mjr-wrapper scroll">
                            <div className="mjr-body">
                                <MensajeriaFiltrosPanel
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    sucursales={sucursalesDisponibles}
                                />

                                <div className="mjr-columnas">
                                    {filtros.lineas.includes("Posventa") && (
                                        <MensajeriaColumnaChats
                                            titulo="Chats Posventa"
                                            icono={<RequestQuoteIcon />}
                                            chats={chatsPosventa}
                                            onAbrirChat={(chat) => handleAbrirChat(chat, "POSVENTA")}
                                        />
                                    )}
                                    {filtros.lineas.includes("Cobranza") && (
                                        <MensajeriaColumnaChats
                                            titulo="Chats Cobranza"
                                            icono={<ChatBubbleOutlineIcon />}
                                            chats={chatsCobranza}
                                            onAbrirChat={(chat) => handleAbrirChat(chat, "COBRANZA")}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ChatScreen
                open={modalChat}
                onClose={handleCerrarChat}
                numeroTelefono={chatActivo?.telefono}
                cliente={chatActivo?.nombre}
                modulo={chatActivo?.modulo}
            />
        </div>
    );
};
